import React, { useState, useEffect } from "react";
import { FaMinus, FaPlus } from "react-icons/fa";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Thumbs } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/thumbs";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getPublicProducts } from "../redux/user/landingpageproducts/landingpage_service_syn";
import { UserMajorCartCreation, UserGetCart } from "../redux/user/cart/cartServicesSync";
import { InfinitySpin } from "react-loader-spinner";
import { updateCartIndicator } from "../redux/user/cart/cartSlices";
import ShareButtons from "../component/ShareButtons";

export default function ProductDetails() {
  const [quantity, setQuantity] = useState(1);
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedColor, setSelectedColor] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { products } = useSelector((state) => state.landpage);
  const { cartIndicator } = useSelector((state) => state.user_cart);
  const { id } = useParams();
  const location = useLocation();
  const productFromState = location.state?.product || null;
  const productFromRedux = products?.docs?.find((product) => product._id === id);
  const currentProduct = productFromState || productFromRedux;


  useEffect(() => {
    dispatch(UserGetCart());
  }, [dispatch]);

  // If product not in state or Redux, fetch products (fallback)
  useEffect(() => {
    if (!productFromState && !productFromRedux) {
      dispatch(getPublicProducts({ name: '""', category: '""', from: 0, to: 0, limit: 100 }));
    }
  }, [dispatch, productFromState, productFromRedux]);

  const handleCheckout = () => {
    navigate("/user_dashboard/cart");
  };

  const handleQuantityChange = (type) => {
    if (type === "minus" && quantity > 1) setQuantity((prev) => prev - 1);
    if (type === "plus") setQuantity((prev) => prev + 1);
  };

  const handleAddToCart = () => {
    if (!currentProduct) return;

    dispatch(updateCartIndicator(currentProduct._id));
    dispatch(
      UserMajorCartCreation({
        productId: currentProduct._id,
        quantity: quantity,
        price: currentProduct.price,
        color: selectedColor,
      })
    )
      .then(() => {
        dispatch(UserGetCart());
        dispatch(updateCartIndicator(""));
      })
      .catch(() => dispatch(updateCartIndicator("")));
  };

  if (!currentProduct) {
    return (
      <div className="text-center py-10">
        <h2 className="text-2xl font-bold">Product not found</h2>
        <p className="text-gray-500">Please check the product link or try again later.</p>
      </div>
    );
  }

  return (
    <>
      {/* Breadcrumb */}
      <section className="breadcrumb__section breadcrumb__bg">
        <div className="container">
          <div className="row row-cols-1">
            <div className="col">
              <div className="breadcrumb__content">
                <h1 className="breadcrumb__content--title text-white mb-10">Product Details</h1>
                <ul className="breadcrumb__content--menu d-flex">
                  <li className="breadcrumb__content--menu__items">
                    <a className="text-white" href="/">Home</a>
                  </li>
                  <li className="breadcrumb__content--menu__items">
                    <span className="text-white">Product Details</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Details Section */}
      <div className="max-w-[113rem] mx-auto p-4 md:p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Images */}
          <div className="flex flex-col items-center w-full">
            <Swiper
              spaceBetween={6}
              navigation={true}
              thumbs={{ swiper: thumbsSwiper }}
              modules={[Navigation, Thumbs]}
              className="w-full"
            >
              {currentProduct?.gallery?.map((img, index) => (
                <SwiperSlide key={index}>
                  <img
                    src={img.pic_url}
                    alt={`Product ${index + 1}`}
                    className="w-full h-[400px] object-cover rounded-lg cursor-pointer"
                    loading="eager"
                    onClick={() => setSelectedImage(img.pic_url)}
                  />
                </SwiperSlide>
              ))}
            </Swiper>

            <Swiper
              onSwiper={setThumbsSwiper}
              spaceBetween={10}
              slidesPerView={2}
              watchSlidesProgress={true}
              modules={[Thumbs]}
              className="mt-2 cursor-pointer w-full"
            >
              {currentProduct?.gallery?.map((img, index) => (
                <SwiperSlide key={index}>
                  <img
                    src={img.pic_url}
                    alt={`Product ${index + 1}`}
                    className="w-full h-[200px] object-cover rounded-lg"
                    loading="eager"
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>

          {/* Product Info */}
          <div className="space-y-6 bg-white w-full p-4">
            <h1 className="text-3xl font-bold">{currentProduct?.product_name}</h1>
            <div className="flex items-center space-x-4">
              <span className="text-2xl font-bold text-red-500">
                {currentProduct?.symbol}
                {Number(currentProduct?.price).toLocaleString('en-NG', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
              </span>
              <span className="text-gray-500 line-through">
                {currentProduct?.symbol}
                {Number(currentProduct?.discount).toLocaleString('en-NG', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
              </span>
            </div>

            <p className="product__details--info__desc mb-20">{currentProduct?.description}</p>

            {currentProduct?.colors?.length > 0 && (
              <div>
                <h3 className="font-semibold mb-2">Available Colors:</h3>
                <div className="flex gap-3 flex-wrap">
                  {currentProduct.colors.map((color, index) => (
                    <div
                      key={index}
                      onClick={() => setSelectedColor(color)}
                      className={`w-10 h-10 rounded-full cursor-pointer border-2 ${selectedColor === color ? 'ring-2 ring-red-500' : 'border-gray-300'}`}
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Quantity and Add to Cart */}
            <div className="flex items-center space-x-4">
              <button onClick={() => handleQuantityChange("minus")} className="quantity__value decrease">
                <FaMinus />
              </button>
              <input type="number" value={quantity} readOnly className="quantity__number" />
              <button onClick={() => handleQuantityChange("plus")} className="quantity__value increase">
                <FaPlus />
              </button>

              {currentProduct?._id === cartIndicator ? (
                <button className="px-6 py-2 bg-red-500 text-white rounded-md">
                  <InfinitySpin width="80" color="#FFFFFF" />
                </button>
              ) : (
                <button className="px-6 py-2 bg-red-500 text-white rounded-md hover:bg-red-600" onClick={handleAddToCart}>
                  Add To Cart
                </button>
              )}
            </div>

            <button className="w-full py-3 bg-red-500 text-white rounded-md hover:bg-red-600 cursor-pointer" onClick={handleCheckout}>
              View Cart
            </button>

            <div className="flex items-center gap-4 mt-[3rem]">
              <span className="font-semibold">Social Share :</span>
              <ShareButtons />
            </div>
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-75 z-50" onClick={() => setSelectedImage(null)}>
          <div className="relative">
            <img src={selectedImage} alt="Enlarged view" className="max-w-[90vw] max-h-[90vh] rounded-lg" />
            <button className="absolute top-2 right-2 bg-white text-black px-3 py-1 rounded-full" onClick={() => setSelectedImage(null)}>
              ✕
            </button>
          </div>
        </div>
      )}
    </>
  );
}
